import { FactoryMoveRequestStatus } from '../enums/factory-move-request-status.enum';
import { InjectionMoldingSubModule, PrismModule } from '../enums/prism-module.enum';
import { FactoryMoveRequest, FactoryMoveRequestLinkedItem, FactoryMoveRequestOrderLineReference, isFactoryMoveRequestOrderLine } from '../models/factory-move-request.model';
import { DisplayInjectionMoldingOrderedProduct, InjectionMoldingOrderedProduct } from '../models/injection-molding-ordered-product.model';
import { User } from '../models/user.model';

const BOB_GANSOW_EMAIL = 'bob@seljan.com';
const CHRIS_MENG_EMAIL = 'cmeng@seljan.com';

export function injectionMoldingMoveRequestFollowerIds(
    selectedFollowerIds: string[],
    creator: Pick<User, 'email'>,
    users: Array<Pick<User, 'id' | 'email'>>,
): string[] {
    const followerIds = new Set(selectedFollowerIds);
    if (creator.email.trim().toLowerCase() !== BOB_GANSOW_EMAIL) return [...followerIds];

    const chris = users.find((user) => user.email.trim().toLowerCase() === CHRIS_MENG_EMAIL);
    if (chris) followerIds.add(chris.id);
    return [...followerIds];
}

export function quantityAlreadyRequestedForInjectionMoldingOrderLine(
    requests: FactoryMoveRequest[],
    orderedProductId: string,
): number {
    return requests
        .filter((request) => request.status !== FactoryMoveRequestStatus.Canceled)
        .flatMap((request) => request.linkedItems ?? [])
        .filter(isFactoryMoveRequestOrderLine)
        .filter((item) => item.sourceModule === PrismModule.InjectionMolding
            && item.orderLine.orderLineId === orderedProductId)
        .reduce((sum, item) => sum + Number(item.orderLine.moveQuantity || 0), 0);
}

/** Open and completed requests reserve their quantities; canceled requests free them again. */
export function unrequestedCompletedInjectionMoldingQuantity(
    orderedProduct: Pick<InjectionMoldingOrderedProduct, 'quantity' | 'completedQuantity'>,
    references: FactoryMoveRequestOrderLineReference[],
): number {
    const requested = references
        .filter(({ request }) => request.status !== FactoryMoveRequestStatus.Canceled)
        .reduce((sum, { linkedItem }) => sum + Number(linkedItem.orderLine.moveQuantity || 0), 0);
    return Math.max(Math.min(orderedProduct.completedQuantity ?? 0, orderedProduct.quantity) - requested, 0);
}

export function toInjectionMoldingMoveRequestLinkedItem(
    display: DisplayInjectionMoldingOrderedProduct,
    moveQuantity: number,
): FactoryMoveRequestLinkedItem {
    const { orderedProduct, orderedProductVersion, order, orderCustomer } = display;
    return {
        id: `${PrismModule.InjectionMolding}:order-line:${orderedProduct.id}`,
        sourceModule: PrismModule.InjectionMolding,
        entityType: 'order-line',
        entityId: orderedProduct.id,
        entityVersionId: orderedProductVersion.id,
        partNumber: orderedProductVersion.partNumber,
        description: orderedProductVersion.description,
        thumbnailImageUrl: orderedProductVersion.thumbnailImageUrl,
        units: 'ea',
        moveQuantity,
        route: `/${PrismModule.InjectionMolding}/${InjectionMoldingSubModule.Orders}`
            + `/view-order/${order.id}#_${orderedProduct.id}`,
        orderLine: {
            orderId: order.id,
            orderLineId: orderedProduct.id,
            orderNumber: order.orderNumber,
            customerId: order.customerId,
            customerName: orderCustomer?.name || 'unknown customer',
            customerPurchaseOrderNumber: order.customerPurchaseOrderNumber || '',
            salesOrderNumber: orderedProduct.salesOrderNumber || '',
            customerPartNumber: orderedProductVersion.customerPartNumber || '',
            dueDate: orderedProduct.dueDate.toISOString(),
            status: orderedProduct.status,
            orderedQuantity: orderedProduct.quantity,
            completedQuantityAtRequest: orderedProduct.completedQuantity ?? 0,
            moveQuantity,
        },
    };
}
